import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Sidebar,
  Menu,
  MenuItem,
  SubMenu 
} from 'react-pro-sidebar';
import { styled } from '@mui/material/styles';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AssessmentIcon from '@mui/icons-material/Assessment';
import GavelIcon from '@mui/icons-material/Gavel';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import SettingsIcon from '@mui/icons-material/Settings';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import BusinessIcon from '@mui/icons-material/Business';
import AppsIcon from '@mui/icons-material/Apps';

const StyledSidebar = styled(Sidebar)(({ theme }) => ({
  height: '100%',
  minHeight: '100vh',
  background: '#171923',
  '& .ps-sidebar-container': {
    borderRight: '1px solid rgba(82,153,255,0.1)',
    padding: '1rem 0',
  },
  '& .ps-menu-root .ps-menuitem-root .ps-menu-button': {
    color: '#ffffff',
    padding: '12px 24px',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    position: 'relative',
    transition: 'all 0.3s ease',
    backgroundColor: 'transparent !important',
    
    '&:hover': {
      background: 'linear-gradient(90deg, rgba(82,153,255,0.15) 0%, rgba(47,201,176,0.15) 100%) !important',
      transform: 'translateX(3px)',
      boxShadow: '0 0 20px rgba(82,153,255,0.15)',
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        height: '100%',
        width: '3px',
        background: 'linear-gradient(90deg, #5299FF 0%, #2FC9B0 100%)',
        boxShadow: '0 0 8px rgba(82,153,255,0.5)',
      },
    }
  },

  // Icon styles
  '& .ps-menu-icon': {
    color: '#5299FF',
    minWidth: '24px',
    width: '24px',
    height: '24px',
    marginRight: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease',
  },

  // Label styles
  '& .ps-menu-label': {
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: 500,
    whiteSpace: 'nowrap',
    transition: 'all 0.3s ease',
  },

  // Enhanced hover effects
  '& .ps-menu-button:hover': {
    '& .ps-menu-icon': {
      transform: 'scale(1.1)',
      filter: 'brightness(1.2)',
      background: 'linear-gradient(90deg, #5299FF 0%, #2FC9B0 100%)',
      WebkitBackgroundClip: 'text',
      backgroundClip: 'text',
      color: 'transparent',
    },
    '& .ps-menu-label': {
      background: 'linear-gradient(90deg, #5299FF 0%, #2FC9B0 100%)',
      WebkitBackgroundClip: 'text',
      backgroundClip: 'text',
      color: 'transparent',
      transform: 'translateX(3px)',
    }
  },

  // Active state
  '& .ps-menu-root .ps-menuitem-root .ps-menu-button.ps-active': {
    background: 'linear-gradient(90deg, rgba(82,153,255,0.2) 0%, rgba(47,201,176,0.2) 100%) !important',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      height: '100%',
      width: '3px',
      background: 'linear-gradient(90deg, #5299FF 0%, #2FC9B0 100%)',
      boxShadow: '0 0 8px rgba(82,153,255,0.5)',
    },
    '& .ps-menu-icon': {
      filter: 'brightness(1.2)',
      background: 'linear-gradient(90deg, #5299FF 0%, #2FC9B0 100%)',
      WebkitBackgroundClip: 'text',
      backgroundClip: 'text',
      color: 'transparent',
    },
    '& .ps-menu-label': {
      background: 'linear-gradient(90deg, #5299FF 0%, #2FC9B0 100%)',
      WebkitBackgroundClip: 'text',
      backgroundClip: 'text',
      color: 'transparent',
    }
  }
}));

export const SponsFINavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = [
    {
      label: 'Operations',
      icon: <BusinessIcon />,
      path: '/operations/applications'
    },
    {
      label: 'Analytics',
      icon: <AnalyticsIcon />,
      path: '/analytics'
    },
    {
      label: 'SponsFL',
      icon: <AppsIcon />,
      submenu: [
        {
          label: 'Overview',
          icon: <DashboardIcon />,
          path: '/sponsfi/overview'
        },
        {
          label: 'Accounting',
          icon: <AccountBalanceIcon />,
          path: '/sponsfi/accounting'
        },
        {
          label: 'Reports',
          icon: <AssessmentIcon />,
          path: '/sponsfi/reports'
        },
        {
          label: 'Compliance',
          icon: <GavelIcon />,
          path: '/sponsfi/compliance'
        },
        {
          label: 'Grants',
          icon: <CardGiftcardIcon />,
          path: '/sponsfi/grants'
        },
        {
          label: 'Settings',
          icon: <SettingsIcon />,
          path: '/sponsfi/settings'
        }
      ]
    }
  ];

  return (
    <StyledSidebar>
      <Menu>
        {menuItems.map((item, index) => (
          item.submenu ? (
            <SubMenu
              key={index}
              label={item.label}
              icon={item.icon}
            >
              {item.submenu.map((subItem, subIndex) => (
                <MenuItem
                  key={`${index}-${subIndex}`}
                  icon={subItem.icon}
                  onClick={() => navigate(subItem.path)}
                  active={location.pathname === subItem.path}
                >
                  {subItem.label}
                </MenuItem>
              ))}
            </SubMenu>
          ) : (
            <MenuItem
              key={index}
              icon={item.icon}
              onClick={() => navigate(item.path)}
              active={location.pathname === item.path}
            >
              {item.label}
            </MenuItem>
          )
        ))}
      </Menu>
    </StyledSidebar>
  );
};

export default SponsFINavigation; 